import React, { useContext, useEffect } from "react";
import styled, { css } from "styled-components";

import { FormContext } from "../../contexts/FormContext";
import { useArray } from "../../hooks/useArray";
import { inputGlowOnChildrenVisible } from "../../utils/styles";

import { Label, LabelText } from "./Label";

const Grid = styled.div`
  padding: 0.1rem;
  border: solid 1px #dadada;
  background-color: #fff;
  border-radius: 0.2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(135px, 1fr));
`;

interface Props {
  value?: any;
  onChange?: (values: any) => void;
  disabled?: boolean;
}

type Styled<Props> = Props & {
  className?: string;
};

const CheckboxGridInput: React.FC<React.PropsWithChildren<Styled<Props>>> & {
  Item: typeof CheckboxGridItem;
} = (props) => {
  const [values, { toggle, set }] = useArray(props?.value);

  const form = useContext(FormContext);

  useEffect(() => {
    set(props.value);
  }, [set, props.value]);

  return (
    <Grid className={props.className}>
      {React.Children.map(props.children, (c: any) => {
        return React.cloneElement(c, {
          key: c.props.value,
          checked: values && values.includes(c.props.value) ? true : false,
          disabled: props.disabled ?? form.disabled,
          onChange: () => {
            if (!props.disabled) {
              props.onChange?.(toggle(c.props.value));
            }
          },
        });
      })}
    </Grid>
  );
};

const StyledItem = styled(Label)<{ truncate?: boolean }>`
  display: flex;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s;
  margin: 0.1rem 0;
  align-items: center;

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    background-color: #e6e5ea;
    color: #333;
  }
  .ui-form-checkbox-cb {
    width: 2.5rem;
  }
  .ui-form-checkbox-cb input {
    margin: 0;
  }
  .ui-form-checkbox-label {
    flex: 1;
  }
  .ui-form-checkbox-count {
    font-size: 0.85em;
    background-color: #f2f2f2;
    border-radius: 0.3rem;
    padding: 0.1rem 0.5rem;
    text-align: center;
    width: 2rem;
    transition: background-color 0.2s;
  }

  & > input[type="checkbox"] {
    order: -1;
    margin: 0;
  }

  ${({ truncate }) =>
    truncate &&
    css`
      ${LabelText} {
        overflow: hidden;
        text-overflow: ellipsis;
        text-wrap: nowrap;
      }
    `}

  border: solid 1px transparent;
  ${inputGlowOnChildrenVisible}
`;

export const CheckboxGridItem: React.FC<
  React.PropsWithChildren<{
    value: string;
    checked?: boolean;
    disabled?: boolean;
    count?: number;
    onChange?: () => void;

    truncate?: boolean;
  }>
> = (props) => {
  return (
    <StyledItem
      layout="row"
      label={props.children}
      truncate={props.truncate ?? true}
    >
      <input
        type="checkbox"
        value={props.value}
        checked={props.checked}
        disabled={props.disabled}
        onChange={props.onChange}
      />
      {props.count && (
        <div className="ui-form-checkbox-count">{props.count}</div>
      )}
    </StyledItem>
  );
};

CheckboxGridInput.Item = CheckboxGridItem;

export default CheckboxGridInput;
